import { asArray } from 'lib/array/arrayUtils'

import { MerchandiseFilterFacetForm } from './MerchandiseSearchFilters'
import { setManySearchParamValues, setSearchParamValue } from 'lib/url/searchUrlUtils'
import { objectEntries } from 'lib/object/objectUtils'
import qs from 'qs'

export const EmptyMerchandiseFilters: App.MerchandiseFilters = {
  brands: [],
  campaigns: [],
  categories: [],
  options: [],
  points: undefined,
  price: undefined,
  sortBy: undefined,
}

export function mapMerchandiseFilterFormToFilters(formFilters: MerchandiseFilterFacetForm): App.MerchandiseFilters {
  const { brands, campaigns, categories, points, price, sortBy, ...options } = formFilters

  return {
    brands: asArray(brands),
    campaigns: asArray(campaigns),
    categories: asArray(categories),
    price: price && {
      min: price.start,
      max: price.end,
    },
    sortBy,
    options: Object.entries(options).map(([key, value]) => ({
      type: key,
      value: asArray(value),
    })),
  }
}

// Some keys in the merchandise filters doesn't match the query param key
const querykeyOverrides = new Map<keyof App.MerchandiseFilters, string>([
  ['name', 'search'],
])

export function getMerchandiseQueryFromFilters(
  filters: App.MerchandiseFilters,
): string {
  return objectEntries(filters).reduce<string>((nextSearch, [key, value]) => {
    if (value) {
      if (key === 'price') {
        // Include price/points if either min or max is defined
        // This allows setting just one boundary if the user only adjusted one side
        const rangeValue = value as { min?: number, max?: number }
        if (rangeValue.min !== undefined || rangeValue.max !== undefined) {
          const params: Array<{ paramName: string, paramValue: number }> = []
          if (rangeValue.min !== undefined) {
            params.push({ paramName: `${key}.min`, paramValue: rangeValue.min })
          }
          if (rangeValue.max !== undefined) {
            params.push({ paramName: `${key}.max`, paramValue: rangeValue.max })
          }
          return setManySearchParamValues(nextSearch, params)
        }
      } else if (key === 'options') {
        return setManySearchParamValues(nextSearch, (value as any).map((option: any) => ({
          paramName: `${key}.${option.type}`,
          paramValue: option.value,
        })))
      } else if (typeof value === 'string' || Array.isArray(value)) {
        return setSearchParamValue(nextSearch, querykeyOverrides.get(key) ?? key, value as any)
      }
    }
    return nextSearch
  }, '')
}

export function getMerchandiseFiltersFromQuery(queryString: string): App.MerchandiseFilters {
  const params = qs.parse(queryString, { ignoreQueryPrefix: true }) as any
  const { brands, campaigns, categories, points, price, sortBy, search, ...options } = params

  return {
    brands: asArray(brands),
    campaigns: asArray(campaigns),
    categories: asArray(categories),
    // Keep reading legacy points from URL for backwards-compat, but app now uses price
    points: {
      min: params['points.min'] ? Number(params['points.min']) : undefined,
      max: params['points.max'] ? Number(params['points.max']) : undefined,
    },
    price: {
      min: params['price.min'] ? Number(params['price.min']) : undefined,
      max: params['price.max'] ? Number(params['price.max']) : undefined,
    },
    sortBy: sortBy ?? 'recommended',
    name: search,
    options: objectEntries<Record<string, string | Array<string>>>(options)
      .filter(([key]) => key.startsWith('options.'))
      .map(([key, value]) => ({
        type: key.replace('options.', ''),
        value: asArray(value),
      })),
  }
}

export function getMerchandiseAppliedFilterCount(
  filters: App.MerchandiseFilters,
  excludeCategories = false,
): number {
  const categoriesCount = excludeCategories ? 0 : (filters.categories?.length ?? 0)
  const brandsCount = filters.brands?.length ?? 0
  const campaignsCount = filters.campaigns?.length ?? 0
  const optionsCount = filters.options?.reduce((sum, option) => sum + option.value.length, 0) ?? 0
  const priceCount = (filters.price?.min !== undefined || filters.price?.max !== undefined) ? 1 : 0
  const pointsCount = (filters.points?.min !== undefined || filters.points?.max !== undefined) ? 1 : 0

  return categoriesCount + brandsCount + campaignsCount + optionsCount + priceCount + pointsCount
}
